import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Type, AlignLeft, Hash, Calendar as CalendarIcon, ChevronDownCircle, List as ListIcon,
    CheckSquare, Link as LinkIcon, Mail, User as UserIcon, MoreVertical, Plus, Trash2,
    MessageSquare, ExternalLink, X, Check
} from 'lucide-react';

export interface SheetColumn {
    id: string;
    name: string;
    type: string;
    options: string[];
    width: number;
    position: number;
    is_primary: boolean;
}

export interface SheetRow {
    id: string;
    position: number;
    data: Record<string, any>;
    comment_count: number;
    open_comment_count: number;
}

interface SheetGridProps {
    columns: SheetColumn[];
    rows: SheetRow[];
    users: any[];
    busy?: boolean;
    onCellChange: (rowId: string, columnId: string, value: any) => Promise<void>;
    onPasteBlock: (startRowIndex: number, startColIndex: number, matrix: string[][]) => Promise<void>;
    onAddRow: () => void;
    onDeleteRow: (rowId: string) => void;
    onAddColumn: () => void;
    onColumnUpdate: (columnId: string, patch: Record<string, any>) => void;
    onColumnDelete: (columnId: string) => void;
    onOpenComments: (row: SheetRow) => void;
}

const TYPE_META: Record<string, { label: string; Icon: any }> = {
    text: { label: 'Text', Icon: Type },
    long_text: { label: 'Long text', Icon: AlignLeft },
    number: { label: 'Number', Icon: Hash },
    date: { label: 'Date', Icon: CalendarIcon },
    select: { label: 'Single select', Icon: ChevronDownCircle },
    multi_select: { label: 'Multi select', Icon: ListIcon },
    checkbox: { label: 'Checkbox', Icon: CheckSquare },
    url: { label: 'URL', Icon: LinkIcon },
    email: { label: 'Email', Icon: Mail },
    user: { label: 'Person', Icon: UserIcon },
};

/** Parse clipboard text the way Excel and Google Sheets emit it. */
export const parseClipboardMatrix = (text: string): string[][] => {
    const normalised = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const lines = normalised.split('\n');
    while (lines.length && lines[lines.length - 1] === '') lines.pop();
    return lines.map((line) => line.split('\t'));
};

export const SheetGrid: React.FC<SheetGridProps> = ({
    columns, rows, users, busy,
    onCellChange, onPasteBlock, onAddRow, onDeleteRow,
    onAddColumn, onColumnUpdate, onColumnDelete, onOpenComments,
}) => {
    const [selected, setSelected] = useState<{ r: number; c: number } | null>(null);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState<string>('');
    const [menuColumn, setMenuColumn] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

    const userLabel = useMemo(() => {
        const map: Record<string, string> = {};
        users.forEach((u: any) => { map[String(u.id)] = u.display_name || u.email_address || String(u.id); });
        return map;
    }, [users]);

    useEffect(() => {
        if (editing && inputRef.current) {
            inputRef.current.focus();
            if ('select' in inputRef.current) (inputRef.current as HTMLInputElement).select();
        }
    }, [editing]);

    const displayValue = useCallback((col: SheetColumn, value: any): string => {
        if (value === null || value === undefined || value === '') return '';
        if (col.type === 'checkbox') return value ? '✓' : '';
        if (col.type === 'user') return userLabel[String(value)] || String(value);
        if (Array.isArray(value)) return value.join(', ');
        return String(value);
    }, [userLabel]);

    const rawEditValue = (col: SheetColumn, value: any): string => {
        if (value === null || value === undefined) return '';
        if (Array.isArray(value)) return value.join(', ');
        return String(value);
    };

    const commit = async (rowIndex: number, colIndex: number, value: any) => {
        const row = rows[rowIndex];
        const col = columns[colIndex];
        if (!row || !col) return;
        setError(null);
        try {
            await onCellChange(row.id, col.id, value);
        } catch (e: any) {
            setError(e?.message || 'Could not save that value');
        }
    };

    const startEdit = (r: number, c: number) => {
        const col = columns[c];
        if (!col) return;
        if (col.type === 'checkbox') {
            commit(r, c, !rows[r]?.data?.[col.id]);
            return;
        }
        setSelected({ r, c });
        setDraft(rawEditValue(col, rows[r]?.data?.[col.id]));
        setEditing(true);
    };

    const move = (dr: number, dc: number) => {
        setSelected((prev) => {
            if (!prev) return prev;
            const r = Math.min(Math.max(prev.r + dr, 0), rows.length - 1);
            const c = Math.min(Math.max(prev.c + dc, 0), columns.length - 1);
            return { r, c };
        });
    };

    const handleKeyDown = async (e: React.KeyboardEvent) => {
        if (!selected) return;
        const col = columns[selected.c];

        if (editing) {
            if (e.key === 'Escape') { e.preventDefault(); setEditing(false); return; }
            if (e.key === 'Enter' && !(col?.type === 'long_text' && e.shiftKey)) {
                e.preventDefault();
                await commit(selected.r, selected.c, draft);
                setEditing(false);
                move(1, 0);
                return;
            }
            if (e.key === 'Tab') {
                e.preventDefault();
                await commit(selected.r, selected.c, draft);
                setEditing(false);
                move(0, e.shiftKey ? -1 : 1);
                return;
            }
            return;
        }

        switch (e.key) {
            case 'ArrowDown': e.preventDefault(); move(1, 0); break;
            case 'ArrowUp': e.preventDefault(); move(-1, 0); break;
            case 'ArrowRight': e.preventDefault(); move(0, 1); break;
            case 'ArrowLeft': e.preventDefault(); move(0, -1); break;
            case 'Tab': e.preventDefault(); move(0, e.shiftKey ? -1 : 1); break;
            case 'Enter': e.preventDefault(); startEdit(selected.r, selected.c); break;
            case 'Delete':
            case 'Backspace': e.preventDefault(); await commit(selected.r, selected.c, null); break;
            default:
                if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
                    setDraft(e.key);
                    setEditing(true);
                }
        }
    };

    const handlePaste = async (e: React.ClipboardEvent) => {
        if (!selected || editing) return;
        const text = e.clipboardData.getData('text/plain');
        if (!text) return;
        const matrix = parseClipboardMatrix(text);
        if (matrix.length === 1 && matrix[0].length === 1) {
            e.preventDefault();
            await commit(selected.r, selected.c, matrix[0][0]);
            return;
        }
        e.preventDefault();
        setError(null);
        try {
            await onPasteBlock(selected.r, selected.c, matrix);
        } catch (err: any) {
            setError(err?.message || 'Paste failed');
        }
    };

    const renderEditor = (col: SheetColumn, r: number, c: number) => {
        const common = "w-full h-full px-2 py-1 text-xs border-2 border-blue-500 rounded outline-none bg-white";
        if (col.type === 'select') {
            return (
                <select
                    ref={(el) => { inputRef.current = el as any; }}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onBlur={async () => { await commit(r, c, draft); setEditing(false); }}
                    className={common}
                >
                    <option value="">—</option>
                    {col.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
            );
        }
        if (col.type === 'user') {
            return (
                <select
                    ref={(el) => { inputRef.current = el as any; }}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onBlur={async () => { await commit(r, c, draft); setEditing(false); }}
                    className={common}
                >
                    <option value="">—</option>
                    {users.map((u: any) => (
                        <option key={u.id} value={u.id}>{u.display_name || u.email_address}</option>
                    ))}
                </select>
            );
        }
        if (col.type === 'long_text') {
            return (
                <textarea
                    ref={(el) => { inputRef.current = el; }}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onBlur={async () => { await commit(r, c, draft); setEditing(false); }}
                    rows={3}
                    className={`${common} resize-y absolute z-20 min-h-[70px] shadow-lg`}
                />
            );
        }
        return (
            <input
                ref={(el) => { inputRef.current = el; }}
                type={col.type === 'date' ? 'date' : col.type === 'number' ? 'text' : 'text'}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={async () => { await commit(r, c, draft); setEditing(false); }}
                className={common}
            />
        );
    };

    const renderCell = (col: SheetColumn, row: SheetRow, r: number, c: number) => {
        const isSel = selected?.r === r && selected?.c === c;
        const isEdit = isSel && editing;
        const value = row.data?.[col.id];

        if (isEdit) return renderEditor(col, r, c);

        if (col.type === 'checkbox') {
            return (
                <div className="flex items-center justify-center h-full">
                    <input
                        type="checkbox"
                        checked={!!value}
                        onChange={() => commit(r, c, !value)}
                        className="w-3.5 h-3.5 accent-blue-600 cursor-pointer"
                    />
                </div>
            );
        }
        if (col.type === 'url' && value) {
            return (
                <div className="flex items-center gap-1 px-2 py-1 truncate">
                    <a href={String(value)} target="_blank" rel="noopener noreferrer"
                       onClick={(e) => e.stopPropagation()}
                       className="text-blue-600 hover:underline truncate">{String(value)}</a>
                    <ExternalLink className="w-3 h-3 text-gray-400 shrink-0" />
                </div>
            );
        }
        if (col.type === 'select' && value) {
            return (
                <div className="px-2 py-1">
                    <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 border border-blue-100 rounded text-[11px] font-medium">
                        {String(value)}
                    </span>
                </div>
            );
        }
        return (
            <div className={`px-2 py-1 truncate ${col.type === 'number' ? 'text-right tabular-nums' : ''}`}>
                {displayValue(col, value)}
            </div>
        );
    };

    return (
        <div className="space-y-2">
            {error && (
                <div className="flex items-start gap-2 px-3 py-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg">
                    <span className="flex-1">{error}</span>
                    <button onClick={() => setError(null)}><X className="w-3.5 h-3.5" /></button>
                </div>
            )}

            <div
                ref={containerRef}
                tabIndex={0}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                className="border border-gray-200 rounded-xl bg-white overflow-auto outline-none max-h-[calc(100vh-320px)]"
            >
                <table className="text-xs border-collapse" style={{ minWidth: '100%' }}>
                    <thead className="sticky top-0 z-10">
                        <tr className="bg-gray-50">
                            <th className="sticky left-0 z-20 bg-gray-50 border-b border-r border-gray-200 w-10 text-[11px] font-medium text-gray-400">#</th>
                            {columns.map((col) => {
                                const meta = TYPE_META[col.type] || TYPE_META.text;
                                const { Icon } = meta;
                                return (
                                    <th key={col.id} style={{ width: col.width, minWidth: col.width }}
                                        className="border-b border-r border-gray-200 px-2 py-1.5 text-left font-medium text-gray-600 relative">
                                        <div className="flex items-center gap-1.5">
                                            <Icon className="w-3 h-3 text-gray-400 shrink-0" />
                                            <span className="truncate flex-1" title={`${col.name} · ${meta.label}`}>{col.name}</span>
                                            <button onClick={() => setMenuColumn(menuColumn === col.id ? null : col.id)}
                                                    className="text-gray-300 hover:text-gray-600 shrink-0">
                                                <MoreVertical className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                        {menuColumn === col.id && (
                                            <ColumnMenu
                                                column={col}
                                                onClose={() => setMenuColumn(null)}
                                                onUpdate={(patch) => { onColumnUpdate(col.id, patch); setMenuColumn(null); }}
                                                onDelete={() => { onColumnDelete(col.id); setMenuColumn(null); }}
                                            />
                                        )}
                                    </th>
                                );
                            })}
                            <th className="border-b border-gray-200 px-1 w-10">
                                <button onClick={onAddColumn} title="Add field"
                                        className="text-gray-400 hover:text-blue-600 flex items-center justify-center w-full">
                                    <Plus className="w-3.5 h-3.5" />
                                </button>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, r) => (
                            <tr key={row.id} className="group hover:bg-blue-50/30">
                                <td className="sticky left-0 z-10 bg-white group-hover:bg-blue-50/30 border-b border-r border-gray-200 text-center text-[11px] text-gray-400 relative">
                                    <span className="group-hover:opacity-0">{r + 1}</span>
                                    <div className="absolute inset-0 hidden group-hover:flex items-center justify-center gap-0.5">
                                        <button onClick={() => onOpenComments(row)} title="Comments"
                                                className="text-gray-400 hover:text-blue-600">
                                            <MessageSquare className="w-3 h-3" />
                                        </button>
                                        <button onClick={() => onDeleteRow(row.id)} title="Delete row"
                                                className="text-gray-400 hover:text-red-600">
                                            <Trash2 className="w-3 h-3" />
                                        </button>
                                    </div>
                                </td>
                                {columns.map((col, c) => {
                                    const isSel = selected?.r === r && selected?.c === c;
                                    return (
                                        <td key={col.id}
                                            onClick={() => { setSelected({ r, c }); setEditing(false); containerRef.current?.focus(); }}
                                            onDoubleClick={() => startEdit(r, c)}
                                            style={{ width: col.width, minWidth: col.width }}
                                            className={`border-b border-r border-gray-200 h-7 cursor-cell relative ${isSel ? 'ring-2 ring-inset ring-blue-500 bg-blue-50/40' : ''}`}>
                                            {renderCell(col, row, r, c)}
                                        </td>
                                    );
                                })}
                                <td className="border-b border-gray-200">
                                    {row.comment_count > 0 && (
                                        <button onClick={() => onOpenComments(row)}
                                                className="flex items-center gap-0.5 px-1.5 text-[11px] font-medium text-gray-500 hover:text-blue-600"
                                                title={`${row.comment_count} comment(s), ${row.open_comment_count} open`}>
                                            <MessageSquare className="w-3 h-3" />
                                            {row.comment_count}
                                            {row.open_comment_count > 0 && <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />}
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                        <tr>
                            <td colSpan={columns.length + 2} className="border-b border-gray-200">
                                <button onClick={onAddRow} disabled={busy}
                                        className="flex items-center gap-1.5 w-full px-3 py-1.5 text-xs text-gray-500 hover:text-blue-600 hover:bg-blue-50/50 disabled:opacity-50">
                                    <Plus className="w-3.5 h-3.5" /> Add row
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <p className="text-[11px] text-gray-400">
                Click a cell then type, or press Enter to edit · Tab / arrows move · paste a block straight from Excel
            </p>
        </div>
    );
};

interface ColumnMenuProps {
    column: SheetColumn;
    onClose: () => void;
    onUpdate: (patch: Record<string, any>) => void;
    onDelete: () => void;
}

const ColumnMenu: React.FC<ColumnMenuProps> = ({ column, onClose, onUpdate, onDelete }) => {
    const [name, setName] = useState(column.name);
    const [type, setType] = useState(column.type);
    const [options, setOptions] = useState((column.options || []).join('\n'));

    return (
        <div className="absolute right-0 top-full mt-1 w-60 bg-white border border-gray-200 rounded-xl shadow-xl z-30 p-3 space-y-2 font-normal">
            <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-700">Edit field</span>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-3.5 h-3.5" /></button>
            </div>
            <div>
                <label className="block text-[11px] text-gray-500 mb-0.5">Name</label>
                <input value={name} onChange={(e) => setName(e.target.value)}
                       className="w-full px-2 py-1 text-xs border border-gray-200 rounded" />
            </div>
            <div>
                <label className="block text-[11px] text-gray-500 mb-0.5">Type</label>
                <select value={type} onChange={(e) => setType(e.target.value)}
                        className="w-full px-2 py-1 text-xs border border-gray-200 rounded">
                    {Object.entries(TYPE_META).map(([key, meta]) => (
                        <option key={key} value={key}>{meta.label}</option>
                    ))}
                </select>
            </div>
            {(type === 'select' || type === 'multi_select') && (
                <div>
                    <label className="block text-[11px] text-gray-500 mb-0.5">Choices (one per line)</label>
                    <textarea value={options} onChange={(e) => setOptions(e.target.value)} rows={4}
                              className="w-full px-2 py-1 text-xs border border-gray-200 rounded font-mono" />
                </div>
            )}
            <div className="flex items-center gap-1 pt-0.5">
                <button
                    onClick={() => onUpdate({
                        name,
                        type,
                        options: options.split('\n').map((o) => o.trim()).filter(Boolean),
                    })}
                    className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700">
                    <Check className="w-3 h-3" /> Save
                </button>
                <button onClick={onDelete} title="Delete field"
                        className="px-2 py-1 text-xs text-red-600 border border-red-200 rounded hover:bg-red-50">
                    <Trash2 className="w-3 h-3" />
                </button>
            </div>
            <div className="flex items-center gap-1">
                <span className="text-[11px] text-gray-500">Width</span>
                <button onClick={() => onUpdate({ width: Math.max(80, column.width - 40) })}
                        className="px-1.5 py-0.5 text-[11px] border border-gray-200 rounded hover:bg-gray-50">−</button>
                <button onClick={() => onUpdate({ width: Math.min(900, column.width + 40) })}
                        className="px-1.5 py-0.5 text-[11px] border border-gray-200 rounded hover:bg-gray-50">+</button>
            </div>
        </div>
    );
};
